import { Box, Tooltip } from '@mui/material';
import { ContentCopyOutlined } from '@mui/icons-material';
import { MouseEvent, ReactElement, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

/**
 * A value one takes out of the list — an article number read into a mail or a
 * supplier's shop.
 *
 * The numbers run to 41 characters and 1.020 articles carry 35 or more, so the
 * column shows what fits and the rest is reached by copying rather than by
 * selecting across an ellipsis (LST-S17, GF 12.10.2026). The mark stays out of
 * the way until the row is pointed at, so a column of numbers still reads as a
 * column of numbers.
 */
export interface PaloCopyTextProps {
    value?: string | null;
}

export const PaloCopyText = ({ value }: PaloCopyTextProps): ReactElement | null => {
    const [copied, setCopied] = useState(false);

    if (!value) {
        return null;
    }

    const copy = async (event: MouseEvent): Promise<void> => {
        // The row underneath opens the record; copying is not opening.
        event.stopPropagation();

        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            // Denied clipboard rights are not worth a message: the number is
            // on screen and can be selected by hand.
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', '&:hover .palo-copy': { opacity: 1 } }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
            </Box>
            <Tooltip title={copied ? 'Kopiert' : 'Nummer kopieren'} open={copied || undefined}>
                <Box
                    component="span"
                    className="palo-copy"
                    role="button"
                    aria-label="Nummer kopieren"
                    onClick={copy}
                    sx={{
                        opacity: 0,
                        flexShrink: 0,
                        display: 'flex',
                        cursor: 'pointer',
                        color: copied ? tokens.color.ok : tokens.color.ink3,
                        transition: 'opacity 120ms',
                        '&:hover': { color: tokens.color.accent },
                    }}
                >
                    <ContentCopyOutlined sx={{ fontSize: 13 }} />
                </Box>
            </Tooltip>
        </Box>
    );
};
